import { t } from "@/i18n-js/instance";
import { getAnimationDelay } from "@/react/components/Spaces/AiSummary/animations";
import { AiSummary } from "@/react/components/shared/AiSummary";
import { Sources } from "@/react/components/shared/AiSummary/Sources";
import type { Source } from "@circle-react/types/AiSummary/Source";
import type { PostSpaceSummary } from "@circle-react/types/AiSummary/SpaceSummary";
import { Alert } from "@circle-react-uikit/AlertV2";
import { SummaryHeader } from "./SummaryHeader";
import { TopicsList } from "./TopicsList";

interface SpaceSummaryContentProps {
  allSources: Source[];
  contentCutOffMessage?: string | null;
  events?: string;
  formattedDateRange: string;
  isLoadingSpaceSummary: boolean;
  sourceName: string;
  spaceSummary?: PostSpaceSummary;
  topics: NonNullable<PostSpaceSummary["topics"]>;
}

export const SpaceSummaryContent = ({
  spaceSummary,
  sourceName,
  formattedDateRange,
  isLoadingSpaceSummary,
  allSources,
  topics,
  contentCutOffMessage,
  events,
}: SpaceSummaryContentProps) => (
  <AiSummary.Content>
    <AiSummary.Header
      animate={isLoadingSpaceSummary}
      isHidden={
        !isLoadingSpaceSummary &&
        (Boolean(spaceSummary) || Boolean(contentCutOffMessage))
      }
    >
      {t("ai_spaces_summaries.generating")}
    </AiSummary.Header>

    {contentCutOffMessage && (
      <>
        <SummaryHeader
          formattedDateRange={formattedDateRange}
          sourceName={sourceName}
        />
        <Alert
          type="alert"
          description={contentCutOffMessage}
          className="text-dark !rounded-2xl !border-2 !border-red-200 bg-red-50 !py-2 !pl-4 !pr-3 text-justify dark:!border-red-800 dark:bg-red-900/20"
          showIcon={false}
        />
      </>
    )}

    {spaceSummary && (
      <>
        <SummaryHeader
          formattedDateRange={formattedDateRange}
          sourceName={sourceName}
        />
        <div
          className="animate-fade-in-slow opacity-0"
          style={{
            animationDelay: getAnimationDelay(2),
            animationFillMode: "forwards",
          }}
        >
          <AiSummary.TextWithUserLinks text={spaceSummary.overview || ""} />
        </div>

        <div className="mt-2">
          {topics.length > 0 && (
            <>
              <TopicsList topics={topics} />
              {allSources.length > 0 && (
                <div
                  className="animate-fade-in-slow mt-4 opacity-0"
                  style={{
                    animationDelay: getAnimationDelay(4, topics.length),
                    animationFillMode: "forwards",
                  }}
                >
                  <Sources sources={allSources} />
                </div>
              )}
            </>
          )}
        </div>

        {events && (
          <div
            className="animate-fade-in-slow opacity-0"
            style={{
              animationDelay: getAnimationDelay(5, topics.length),
              animationFillMode: "forwards",
            }}
          >
            <AiSummary.Section
              title={t("ai_spaces_summaries.events")}
              text={events}
            />
          </div>
        )}
      </>
    )}
  </AiSummary.Content>
);
